import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Table, Tag, Space, Button, Select, Tooltip, Typography, Tabs, message } from 'antd';
import { ReloadOutlined, PushpinFilled } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import cdnProbeService from '../../services/cdnProbeService';
import OriginGroups from './OriginGroups';
import OriginGroupRoutes from './OriginGroupRoutes';

const { Text } = Typography;
const fmtTime = (s) => (s ? new Date(s.replace(' ', 'T')).toLocaleString() : '-');
const num = (v) => (v === null || v === undefined ? '-' : Math.round(v));
const pct = (v) => (v === null || v === undefined ? '-' : `${Math.round(v * 100)}%`);

/** 回源自动选路:每个节点 × 源站(/24)直连还是经加速节点;可手动固定 */
const RoutesTab = () => {
    const { t } = useTranslation();
    const [data, setData] = useState({ enabled: false, rows: [], accel: {} });
    const [loading, setLoading] = useState(false);
    const [origin, setOrigin] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        const r = await cdnProbeService.getOriginRoutes();
        setLoading(false);
        if (r.success) setData(r.data || { enabled: false, rows: [], accel: {} });
        else message.error(r.error || t('Failed to load'));
    }, [t]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        const id = setInterval(load, 60000);
        return () => clearInterval(id);
    }, [load]);

    const routeName = useCallback((via) => {
        if (via === null || via === undefined) return '-';
        return via === 'direct' ? t('Direct') : (data.accel[via] || via);
    }, [data.accel, t]);

    const origins = useMemo(() => {
        const m = new Map();
        data.rows.forEach((r) => { if (!m.has(r.prefix)) m.set(r.prefix, r.hosts); });
        return [...m.entries()];
    }, [data.rows]);
    const rows = useMemo(() => data.rows.filter((r) => !origin || r.prefix === origin), [data.rows, origin]);

    const pin = async (row, via) => {
        const r = await cdnProbeService.pinOriginRoute(row.node_id, row.prefix, via);
        if (r.success) { message.success(t('Saved')); load(); }
        else message.error(r.error || t('Save failed'));
    };

    const candTip = (id, c) => (id === 'direct'
        ? `RTT ${num(c.rtt)} ms · ${t('Cold connections')} ${pct(c.cold)}`
        : `${t('To accelerator')} ${num(c.rtt_leg)} ms (${pct(c.cold_leg)}) + ${t('To origin')} ${num(c.rtt_origin)} ms (${pct(c.cold_origin)})`);

    const columns = [
        {
            title: t('Origin'), dataIndex: 'prefix', width: 190,
            render: (p, r) => <Tooltip title={(r.hosts || []).join(', ')}><Text code>{p}</Text></Tooltip>,
        },
        { title: t('Node'), dataIndex: 'node', width: 200 },
        {
            title: t('Route'), width: 190,
            render: (_, r) => {
                const via = r.pinned ?? r.choice;
                return (
                    <Space size={4}>
                        <Tag color={via === null ? 'default' : (via === 'direct' ? 'blue' : 'purple')}>{via === null ? t('Default') : routeName(via)}</Tag>
                        {r.pinned && <Tooltip title={t('Pinned')}><PushpinFilled style={{ color: '#fa8c16' }} /></Tooltip>}
                    </Space>
                );
            },
        },
        {
            title: t('Estimated (ms)'),
            render: (_, r) => {
                const cs = Object.entries(r.candidates || {});
                const best = cs.filter(([, c]) => c.ok && c.ms != null).sort((a, b) => a[1].ms - b[1].ms)[0]?.[0];
                return (
                    <Space size={[4, 4]} wrap>
                        {cs.map(([id, c]) => (
                            <Tooltip key={id} title={candTip(id, c)}>
                                <Tag color={!c.ok ? 'default' : (id === best ? 'green' : undefined)} style={!c.ok ? { textDecoration: 'line-through' } : undefined}>
                                    {routeName(id)} {c.ms == null ? '-' : Math.round(c.ms)}
                                </Tag>
                            </Tooltip>
                        ))}
                    </Space>
                );
            },
        },
        {
            title: t('Last switch'), dataIndex: 'switched_at', width: 170,
            render: (s, r) => (
                <Tooltip title={(r.history || []).slice(0, 8).map((h) => `${fmtTime(h.at)} ${routeName(h.from)} → ${routeName(h.to)}`).join('\n')}
                    overlayStyle={{ whiteSpace: 'pre-line', maxWidth: 420 }}>
                    <Text type="secondary">{fmtTime(s)}</Text>
                </Tooltip>
            ),
        },
        {
            title: t('Pin'), width: 200,
            render: (_, r) => (
                <Select size="small" style={{ width: 180 }} value={r.pinned ?? ''} onChange={(v) => pin(r, v)}
                    options={[
                        { value: '', label: t('Auto') },
                        { value: 'direct', label: t('Direct') },
                        ...Object.keys(r.candidates || {}).filter((id) => id !== 'direct').map((id) => ({ value: id, label: routeName(id) })),
                    ]} />
            ),
        },
    ];

    return (
        <Card
            title={<Space>{t('Origin routing')}{data.mode === 'observe' ? <Tag color="gold">{t('Observe only')}</Tag> : (!data.enabled && <Tag>{t('Off')}</Tag>)}</Space>}
            extra={(
                <Space>
                    <Select allowClear size="small" style={{ width: 220 }} placeholder={t('All origins')} value={origin} onChange={setOrigin}
                        options={origins.map(([p, hosts]) => ({ value: p, label: hosts?.[0] ? `${p} · ${hosts[0]}` : p }))} />
                    <Button icon={<ReloadOutlined />} onClick={load} loading={loading} />
                </Space>
            )}
        >
            <Table rowKey={(r) => `${r.node_id}|${r.prefix}`} size="small" loading={loading} columns={columns} dataSource={rows}
                pagination={{ pageSize: 50, hideOnSinglePage: true }} scroll={{ x: 1100 }} />
        </Card>
    );
};

/** 回源选路页:选路结果 + 源站分组 */
const OriginRoutes = () => {
    const { t } = useTranslation();
    return (
        <Tabs items={[
            { key: 'group-routes', label: t('By origin group'), children: <OriginGroupRoutes /> },
            { key: 'routes', label: t('Routes'), children: <RoutesTab /> },
            { key: 'groups', label: t('Origin groups'), children: <OriginGroups /> },
        ]} />
    );
};

export default OriginRoutes;
